import type {
  EvaluationMode, GradeKey, ParameterDirection, ParameterRange, ParameterRanges,
  ParameterValueMap, SweetSpotInputPoint, SweetSpotParameter, SweetSpotParameterConfig,
  SweetSpotPointResult,
} from './types';

export const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export function clamp01(value: number | null | undefined): number | null {
  if (!isFiniteNumber(value)) return null;
  return Math.max(0, Math.min(1, value));
}

export function normalizeMinMax(
  value: number | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined,
  direction: ParameterDirection,
): number | null {
  if (!isFiniteNumber(value) || !isFiniteNumber(min) || !isFiniteNumber(max)) return null;
  const lo = Math.min(min, max), hi = Math.max(min, max);
  if (hi === lo) return 0.5;
  const normalized = direction === 'smaller_is_better' ? (hi - value) / (hi - lo) : (value - lo) / (hi - lo);
  return clamp01(normalized);
}

export function normalizeTargetRange(value: number, range: ParameterRange, targetMin?: number, targetMax?: number): number | null {
  if (!isFiniteNumber(targetMin) || !isFiniteNumber(targetMax) || targetMin > targetMax) return null;
  if (value >= targetMin && value <= targetMax) return 1;
  if (value < targetMin) {
    if (targetMin <= range.min) return 0;
    return clamp01((value - range.min) / (targetMin - range.min));
  }
  if (targetMax >= range.max) return 0;
  return clamp01((range.max - value) / (range.max - targetMax));
}

export function normalizeWeights(parameters: SweetSpotParameter[]): Record<string, number> {
  const enabled = parameters.filter((parameter) => parameter.enabled);
  if (!enabled.length) return {};
  const safe = enabled.map((parameter) => isFiniteNumber(parameter.weight) && parameter.weight > 0 ? parameter.weight : 0);
  const total = safe.reduce((sum, value) => sum + value, 0);
  return Object.fromEntries(enabled.map((parameter, index) => [parameter.id, total > 0 ? safe[index] / total : 1 / enabled.length]));
}

export function buildParameterRanges(points: SweetSpotInputPoint[]): ParameterRanges {
  const valuesByKey: Record<string, number[]> = {};
  points.forEach((point) => Object.entries(point.values).forEach(([key, value]) => {
    if (isFiniteNumber(value)) (valuesByKey[key] ??= []).push(value);
  }));
  return Object.fromEntries(Object.entries(valuesByKey).map(([key, values]) => [key, { min: Math.min(...values), max: Math.max(...values) }]));
}

export type EffectiveParameter = { parameter: SweetSpotParameter; normalizedWeight: number };

export function resolveEffectiveParameters(parameters: SweetSpotParameter[], ranges: ParameterRanges): EffectiveParameter[] {
  const effective = parameters.filter((parameter) => {
    if (!parameter.enabled || !ranges[parameter.key]) return false;
    return parameter.direction !== 'target_range'
      || (isFiniteNumber(parameter.targetMin) && isFiniteNumber(parameter.targetMax) && parameter.targetMin <= parameter.targetMax);
  });
  const weights = normalizeWeights(effective);
  return effective.map((parameter) => ({ parameter, normalizedWeight: weights[parameter.id] ?? 0 }));
}

export function getParameterUnavailableReason(parameter: SweetSpotParameter, ranges: ParameterRanges): string | null {
  if (!parameter.enabled) return '未启用';
  if (!ranges[parameter.key]) return '无有效数据，不参与本次计算';
  if (parameter.direction === 'target_range'
    && (!isFiniteNumber(parameter.targetMin) || !isFiniteNumber(parameter.targetMax) || parameter.targetMin > parameter.targetMax)) {
    return '目标区间无效，不参与本次计算';
  }
  return null;
}

function normalizedParameterScore(
  parameter: SweetSpotParameter,
  value: number | null | undefined,
  range: ParameterRange,
): number | null {
  if (!isFiniteNumber(value)) return null;
  return parameter.direction === 'target_range'
    ? normalizeTargetRange(value, range, parameter.targetMin, parameter.targetMax)
    : normalizeMinMax(value, range.min, range.max, parameter.direction);
}

function calculateGroupScore(parameters: SweetSpotParameter[], values: ParameterValueMap, ranges: ParameterRanges): number | null {
  const effective = resolveEffectiveParameters(parameters, ranges).map((item) => item.parameter);
  const available = effective.filter((parameter) => isFiniteNumber(values[parameter.key]));
  if (!available.length) return null;
  const weights = normalizeWeights(available);
  const score = available.reduce((sum, parameter) => {
    const normalized = normalizedParameterScore(parameter, values[parameter.key], ranges[parameter.key]!);
    return normalized == null ? sum : sum + normalized * weights[parameter.id];
  }, 0);
  return clamp01(score);
}

export function calculateRQ(values: ParameterValueMap, ranges: ParameterRanges, config: SweetSpotParameterConfig): number | null {
  return calculateGroupScore(config.rq, values, ranges);
}

export function calculateCQ(values: ParameterValueMap, ranges: ParameterRanges, config: SweetSpotParameterConfig): number | null {
  return calculateGroupScore(config.cq, values, ranges);
}

export function getEvaluationMode(rq: number | null, cq: number | null): EvaluationMode {
  if (isFiniteNumber(rq) && isFiniteNumber(cq)) return 'rq_cq';
  if (isFiniteNumber(rq)) return 'rq_only';
  if (isFiniteNumber(cq)) return 'cq_only';
  return 'none';
}

export function calculateSSI(rq: number | null, cq: number | null): number | null {
  const mode = getEvaluationMode(rq, cq);
  if (mode === 'rq_cq') return clamp01(0.5 * rq! + 0.5 * cq!);
  if (mode === 'rq_only') return clamp01(rq);
  if (mode === 'cq_only') return clamp01(cq);
  return null;
}

export function classifySSI(ssi: number | null | undefined): GradeKey | null {
  const score = clamp01(ssi);
  if (score == null) return null;
  if (score >= 0.80) return 'I';
  if (score >= 0.60) return 'II';
  if (score >= 0.40) return 'III';
  if (score >= 0.20) return 'IV';
  return 'V';
}

export function calculateSweetSpotPoints(points: SweetSpotInputPoint[], config: SweetSpotParameterConfig): SweetSpotPointResult[] {
  if (!points.length) return [];
  const ranges = buildParameterRanges(points);
  return points.map((point) => {
    const rq = calculateRQ(point.values, ranges, config);
    const cq = calculateCQ(point.values, ranges, config);
    const ssi = calculateSSI(rq, cq);
    return { depth: point.depth, rq, cq, ssi, grade: classifySSI(ssi) };
  });
}
